import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import MssDataGridPro from '../../common/MssDataGridPro';
import MssAlert from '../../common/MssAlert';
import MssApiError from '../../common/MssApiError';
import { getData } from '../../../DataAccessLayer';
import { courseAnalytics } from '../../../DataAccessLayer/services';
import { COUNT_COLUMNS } from './columns';

const COLUMNS = [
  { field: 'courseId', headerName: 'Course ID', flex: 1 },
  // A fixed width, wider than the others, so long titles wrap rather than squeeze.
  { field: 'courseTitle', headerName: 'Course Title', width: 300 },
  { field: 'termCode', headerName: 'Term', flex: 1 },
  ...COUNT_COLUMNS,
];

/**
 * How many flashcard decks, quizzes and summaries were generated in each
 * course, counting deleted ones and ones still generating but not failed ones,
 * with the totals pinned below.
 */
function CourseAnalytics() {
  const navigate = useNavigate();
  const [usage, setUsage] = useState({ courses: [], totals: null });
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      setUsage(await getData(courseAnalytics));
    } catch (err) {
      console.log(err);
      setLoadError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box className="mss__analytics" id="courseAnalytics">
      <Typography component="h1" className="analyticsTitle">
        Analytics
      </Typography>
      <Typography className="analyticsDescription">
        The number of flashcard decks, practice quizzes and summary notes created in each course.
        Includes deleted resources and resources still being created, but not ones that failed.
      </Typography>

      {loadError ? (
        <MssAlert id="courseAnalytics__loadError" severity="error" type="statusAlert">
          <MssApiError id="courseAnalytics__usage" widgetName="course analytics" reload={load} />
        </MssAlert>
      ) : (
        <MssDataGridPro
          id="courseAnalytics__dataGrid"
          className="mss__dataGrid courseGrid"
          rows={usage.courses}
          columns={COLUMNS}
          getRowId={(row) => row.courseId}
          // The grid wraps cell text in rows of auto height, so a long title
          // takes more lines instead of being cut off.
          getRowHeight={() => 'auto'}
          pinnedRows={{
            bottom: usage.totals
              ? [
                  {
                    ...usage.totals,
                    courseId: 'Total',
                    courseTitle: '',
                    termCode: '',
                    isTotal: true,
                  },
                ]
              : [],
          }}
          onRowClick={({ row }) => {
            if (!row.isTotal)
              navigate(encodeURIComponent(row.courseId), {
                state: { courseTitle: row.courseTitle, termCode: row.termCode },
              });
          }}
          getRowClassName={({ row }) => (row.isTotal ? '' : 'courseRow')}
          isLoading={isLoading}
          pagination
          initialState={{
            pagination: { paginationModel: { pageSize: 20 } },
            sorting: { sortModel: [{ field: 'total', sort: 'desc' }] },
          }}
          slots={{
            noRowsOverlay: () => <Box className="noRowsMessage">No resources generated yet.</Box>,
          }}
          slotProps={{
            loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'linear-progress' },
          }}
        />
      )}
    </Box>
  );
}

export default CourseAnalytics;
